'use client'

import { msg, Trans } from '@lingui/macro'

import * as React from 'react'

import { LinguiMessageDescriptor, useTrans } from '~logic/i18n/useTrans'

import { useModalControl } from '../hooks/useModalControl'
import { ErrorBoundary } from './ErrorBoundary'
import { ErrorHandlerProvider, TError, TErrorPresenterOptions } from './ErrorHandlerProvider'
import { GraphQLErrorGuard } from './GraphQLErrorGuard'
import { useBuildError } from './helpers/useBuildError'

type TCustomerValues = {
  givenName?: Nullable<string>
  familyName?: Nullable<string>
  email?: Nullable<string>
}

export type TErrorModalProps = NoChildren & {
  isOpen: boolean
  onClose: () => void
  headerText: React.ReactNode
  errorText: string | RoA<string>
  allowAppRestart?: boolean
  allowMainButton?: boolean
  mainButtonLabel?: LinguiMessageDescriptor
  sentryEventId?: string
  customerValues?: Nullable<TCustomerValues>
}

type TProps = RequiredChildren & {
  ErrorModal: React.FC<TErrorModalProps>
}

const NO_ERROR: TError = {
  type: 'none',
  payload: undefined,
}

/**
 * Encapsulates handling and presenting of GQL errors & errors from Error Boundary.
 * Provides error handlers.
 */
export const ErrorContainer: React.FC<TProps> = ({ children, ErrorModal }) => {
  const { trans } = useTrans()
  const { buildError } = useBuildError()

  const [error, setError] = React.useState<TError>(NO_ERROR)
  const [errorModal, presentErrorModal, dismissErrorModal] = useModalControl(ErrorModal)

  const dismissError = React.useCallback(() => {
    dismissErrorModal()
    setError(NO_ERROR)
  }, [dismissErrorModal])

  /**
   * Present error callback used throughout the app to show some ~expected errors
   */
  const presentError = React.useCallback(
    (options: TErrorPresenterOptions) => {
      const { errorType } = options

      setError({ type: errorType, payload: undefined })

      if (options.errorType !== `none` && options.errorType !== `handled`) {
        return
      }

      presentErrorModal({
        headerText: options.headerText,
        errorText: options.errorText,
        onClose: dismissError,
        allowAppRestart: errorType === `none`,
        allowMainButton: errorType !== `none`,
      })
    },
    [dismissError, presentErrorModal],
  )

  /**
   * Handle preset error types, caused by GQL communication problems or UI errors
   * The error type is in most cases set from GraphQLErrorGuard or ErrorBoundary
   * What each error type means is roughly described in the place where it’s set
   */
  React.useEffect(() => {
    switch (error.type) {
      case 'serviceUnreachable': {
        presentErrorModal({
          headerText: <Trans>Service is temporarily unavailable</Trans>,
          errorText: buildError(msg`Please try again in moment. We apologize for inconvenience.`),

          onClose: dismissError,
        })

        return
      }

      // In the case of damaged userToken stored in the persistence this error reoccur after the modal is closed,
      // Therefore we need to clear the persistence and reload the app on modal close
      // as we have no way to transfer the current order with invalid userToken
      // As a consequence this will automatically redirect the user to the home screen (because the order is lost)
      // TODO: Consider adding a way to keep the order with the `userId` in persistence to allow the customer to continue with the order after they log in again (if we will have capacity to implement it in the future)
      case 'authError': {
        presentErrorModal({
          headerText: <Trans>Invalid login</Trans>,
          errorText: trans(
            msg`You have been logged out. To continue, we'll need to restart the application. Thank you for your understanding.`,
          ),
          allowMainButton: false,
          allowAppRestart: true,
          onClose: dismissError,
        })

        return
      }

      case 'networkError': {
        presentErrorModal({
          headerText: <Trans>Service problem</Trans>,
          errorText: buildError(msg`Unexpected error occurred in communication with service.`),
          allowAppRestart: true, // ? Maybe not needed anymore since we have new 'authError' type, keeping it just in case
          allowMainButton: true,
          mainButtonLabel: msg`Try again`,
          onClose: dismissError,
        })

        return
      }

      case 'userErrors': {
        const hasUserMessages = Array.isArray(error.payload) && error.payload.length > 0

        presentErrorModal({
          headerText: <Trans>Problems encountered</Trans>,
          errorText: hasUserMessages
            ? (error.payload as RoA<string>)
            : trans(msg`Something went wrong...`),
          onClose: dismissError,
        })

        return
      }

      case 'operationError': {
        // query, mutation (or subscription?) return some errors from BE that the user doesn’t need to see
        // this error is likely to occur again after the modal is closed, therefore it contains also the app restart button
        presentErrorModal({
          headerText: <Trans>Request failure</Trans>,
          errorText: buildError(msg`Unexpected error occurred while sending a request.`),
          allowAppRestart: true,
          allowMainButton: true,
          mainButtonLabel: msg`Try again`,
          onClose: dismissError,
        })

        return
      }

      case 'otherError': {
        presentErrorModal({
          headerText: <Trans>Unexpected error has occurred</Trans>,
          errorText: buildError(msg`Please restart the application.`),
          allowAppRestart: true,
          allowMainButton: false,
          onClose: dismissError,
        })

        return
      }
    }
  }, [error, dismissError, presentErrorModal, buildError, trans])

  const hasSevereError = error.type !== 'none' && error.type !== 'handled'

  return (
    <ErrorHandlerProvider errorHandler={{ presentError, errorType: error.type, setError }}>
      <GraphQLErrorGuard>
        {errorModal}
        <ErrorBoundary>{hasSevereError ? null : children}</ErrorBoundary>
      </GraphQLErrorGuard>
    </ErrorHandlerProvider>
  )
}
